const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  // A calendar-only field has no timezone; retain its day on every staff device.
  const calendar = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (calendar) {
    const [, year, month, day] = calendar.map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
      ? date
      : null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const dateLabel = (date: Date) =>
  `${String(date.getDate()).padStart(2, "0")} ${months[date.getMonth()]} ${String(date.getFullYear()).padStart(4, "0")}`;

/** F-164–F-166: preserve the existing device-local timezone for timestamps. */
export const formatCrmDate = (value: string | null | undefined): string => {
  const date = parseDate(value);
  return date ? dateLabel(date) : "—";
};

/** Source-backed signup/creation timestamps retain their actual minute. */
export const formatCrmDateTime = (value: string | null | undefined): string => {
  const date = parseDate(value);
  if (!date) return "—";
  // A historical calendar-only value has no recorded time.
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) return dateLabel(date);
  const hour = date.getHours();
  return `${dateLabel(date)}, ${hour % 12 || 12}:${String(date.getMinutes()).padStart(2, "0")}${hour >= 12 ? "pm" : "am"}`;
};

const NZ_PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Pacific/Auckland",
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hourCycle: "h23",
});

/**
 * DEC-10: an Activity entry's date and time in New Zealand time, whatever
 * the staff device's zone, in formatCrmDateTime's shape. Lists keep
 * formatCrmDate's short date.
 */
export const formatCrmDateTimeNz = (
  value: string | null | undefined,
): string => {
  const date = parseDate(value);
  if (!date) return "—";
  // A historical calendar-only value has no recorded time.
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) return dateLabel(date);
  const parts = NZ_PARTS.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((entry) => entry.type === type)?.value);
  const hour = part("hour");
  const day = `${String(part("day")).padStart(2, "0")} ${months[part("month") - 1]} ${String(part("year")).padStart(4, "0")}`;
  return `${day}, ${hour % 12 || 12}:${String(part("minute")).padStart(2, "0")}${hour >= 12 ? "pm" : "am"}`;
};

/**
 * F-216's date-and-hour form without the "Last synced" prefix, for other
 * sync-time lines such as a stored Stripe snapshot's update time:
 * "28 Sep 2026, 10am", the hour rounded down.
 */
export const formatCrmDateHour = (value: string | null | undefined): string => {
  const date = parseDate(value);
  if (!date) return "—";
  const hour = date.getHours();
  return `${dateLabel(date)}, ${hour % 12 || 12}${hour >= 12 ? "pm" : "am"}`;
};

const NZ_TIME_ZONE = "Pacific/Auckland";

/**
 * F-216's date-and-hour form fixed to New Zealand time, for the Organisation
 * record's Data sync row (Adrian, 12 Oct 2026): "10 Oct 2026, 10am", the
 * hour rounded down, whatever the viewer's machine timezone.
 */
export const formatNzDateHour = (value: string | null | undefined): string => {
  const date = parseDate(value);
  if (!date) return "—";
  // Numeric parts only: a locale short month can be "Sept", so the month
  // name comes from the same list as every other CRM date.
  const parts = new Intl.DateTimeFormat("en-NZ", {
    timeZone: NZ_TIME_ZONE,
    day: "2-digit",
    month: "numeric",
    year: "numeric",
    hour: "numeric",
    hourCycle: "h23",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((candidate) => candidate.type === type)?.value ?? "");
  const hour24 = part("hour");
  const hour = hour24 % 12 || 12;
  const month = months[part("month") - 1] ?? "";
  return `${String(part("day")).padStart(2, "0")} ${month} ${part("year")}, ${hour}${hour24 >= 12 ? "pm" : "am"}`;
};
